'use client'
import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Clock, Users, Globe, Send, } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { THome_Config } from "@/app/config";
import { toast } from "sonner"

type FormData = {
  name: string;
  number: string;
  email: string;
  category: string;
  message: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

export const ContactSection = ({ config }: { config: THome_Config['contact'] }) => {

  const featuresIcons = [
    {
      icon: Clock,

    },
    {
      icon: Users,

    },
    {
      icon: Globe,

    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };


  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const [formData, setFormData] = useState<FormData>({
    name: "",
    number: "",
    email: "",
    category: "",
    message: "",
  });


  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 4) {
      newErrors.name = "Name must be at least 4 characters long";
    }

    if (!formData.number.trim()) {
      newErrors.number = "Number is required";
    } else if (!/^\d+$/.test(formData.number.trim())) {
      newErrors.number = "Please enter a valid number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const accessKey = process.env.NEXT_PUBLIC_ACCESS_KEY;

      if (!accessKey) {
        throw new Error("Access key not found");
      }
      const payload = {
        public_access_key: accessKey,
        content: {
          Name: formData.name,
          Number: formData.number,
          Email: formData.email,
          Category: formData.category,
          Message: formData.message,
        },
      };

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_ACCESS_API}/collection/data/public`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        toast.success("Form submitted successfully!");
        setFormData({
          name: "",
          email: "",
          number: "",
          category: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.section
      className="w-full py-12 md:py-20 lg:py-[84px] bg-white flex justify-center"
      id="contact"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="max-w-[1120px] w-full px-4 md:px-6 lg:px-8">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-14 items-start"
          variants={containerVariants}
        >
          {/* Left Content Section */}
          <motion.div
            className="flex flex-col gap-6 md:gap-8 lg:gap-[20.3px]"
            variants={{
              hidden: {
                opacity: 0,
                y: 30,
                scale: 0.95
              },
              visible: {
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  duration: 0.5,
                  ease: "easeOut"
                }
              }
            }}
          >
            <motion.header
              className="flex flex-col gap-4 lg:gap-0"
              variants={{
                hidden: {
                  opacity: 0,
                  y: 30,
                  scale: 0.95
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.5,
                    ease: "easeOut"
                  }
                }
              }}
            >
              <motion.h2
                className="font-normal text-black text-2xl md:text-3xl lg:text-[31.5px] leading-tight lg:leading-[35px]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                Send Us a Message
              </motion.h2>
            </motion.header>

            <motion.p
              className="font-normal text-[#495565] text-sm md:text-base lg:text-[15.7px] leading-relaxed lg:leading-[24.5px]"
              variants={{
                hidden: {
                  opacity: 0,
                  y: 30,
                  scale: 0.95
                },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.5,
                    ease: "easeOut"
                  }
                }
              }}
            >
              Fill out the form below and we&apos;ll get back to you as soon as
              possible.
              <br className="hidden md:block" />
              Our team typically responds within 24 hours during business days.
            </motion.p>

            <motion.div
              className="flex flex-col gap-4 md:gap-6 lg:gap-[21px]"
              variants={containerVariants}
            >
              {config.features.map((feature, index) => {
                const Icon = featuresIcons[index].icon;
                return (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 md:gap-4 lg:gap-3.5"
                    variants={{
                      hidden: {
                        opacity: 0,
                        x: -30
                      },
                      visible: (index: number) => ({
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                          delay: index * 0.1
                        }
                      }),
                      hover: {
                        x: 5,
                        transition: {
                          duration: 0.2,
                          ease: "easeInOut"
                        }
                      }
                    }}
                    custom={index}
                    whileHover="hover"
                  >
                    <motion.div
                      className={`flex w-10 h-10 md:w-12 md:h-12 lg:w-[42px] lg:h-[42px] items-center justify-center ${feature.bgColor} rounded-lg`}
                      whileHover={{
                        rotate: 360,
                        scale: 1.1,
                        transition: { duration: 0.6 }
                      }}
                    >
                      <Icon
                        className={`w-5 h-5 md:w-6 md:h-6 lg:w-[21px] lg:h-[21px] ${feature.iconColor}`}
                      />
                    </motion.div>
                    <div className="flex flex-col gap-1 md:gap-2 lg:gap-[7px]">
                      <motion.h3
                        className="text-black text-base md:text-lg lg:text-sm leading-tight"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.4 }}
                      >
                        {feature.title}
                      </motion.h3>
                      <motion.p
                        className="text-[#495565] text-sm md:text-base lg:text-[13.9px] leading-relaxed"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                      >
                        {feature.description}
                      </motion.p>
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          </motion.div>

          {/* Form */}
          <motion.div variants={{
            hidden: {
              opacity: 0,
              x: 50,
              scale: 0.95
            },
            visible: {
              opacity: 1,
              x: 0,
              scale: 1,
              transition: {
                duration: 0.6,
                ease: "easeOut",
                delay: 0.2
              }
            }
          }}>
            <Card className="bg-[#f8fafb] rounded-xl lg:rounded-[12.75px] border">
              <CardContent className="p-6 md:p-8 lg:p-[29px]">
                {/* Success/Error Messages */}
                <AnimatePresence mode="wait">
                  {(submitStatus === "success" || submitStatus === "error") && (
                    <motion.div
                      className="mb-4"
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: -10,
                          scale: 0.9
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: {
                            duration: 0.4,
                            ease: "backOut"
                          }
                        },
                        exit: {
                          opacity: 0,
                          y: -10,
                          scale: 0.9,
                          transition: {
                            duration: 0.2
                          }
                        }
                      }}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {submitStatus === "success" && (
                        <div className="p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm">
                          Message sent successfully! We&apos;ll contact you soon.
                        </div>
                      )}
                      {submitStatus === "error" && (
                        <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                          Sorry, there was an error. Please try again.
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4 md:gap-6 lg:gap-[21px]"
                  variants={containerVariants}
                >
                  {/* Name & Number */}
                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 30,
                        scale: 0.95
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    <motion.div className="flex flex-col gap-2">
                      <Label>
                        Name <span className="text-red-500">*</span>
                      </Label>
                      <motion.div
                        variants={{
                          focus: {
                            scale: 1.02,
                            transition: {
                              duration: 0.2,
                              ease: "easeInOut"
                            }
                          },
                          error: {
                            x: [-5, 5, -5, 5, 0],
                            transition: {
                              duration: 0.4,
                              ease: "easeInOut"
                            }
                          }
                        }}
                        whileFocus="focus"
                        animate={errors.name ? "error" : ""}
                      >
                        <Input
                          placeholder="Enter your name (minimum 4 characters)"
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          className={`${errors.name ? "border-red-500" : "border-[#d0d5db]"
                            } transition-colors duration-200`}
                          disabled={isSubmitting}
                        />
                      </motion.div>
                      <AnimatePresence>
                        {errors.name && (
                          <motion.span
                            className="text-red-500 text-xs"
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.2 }}
                          >
                            {errors.name}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.div>

                    <motion.div className="flex flex-col gap-2">
                      <Label>
                        Number <span className="text-red-500">*</span>
                      </Label>
                      <motion.div
                        variants={{
                          focus: {
                            scale: 1.02,
                            transition: {
                              duration: 0.2,
                              ease: "easeInOut"
                            }
                          },
                          error: {
                            x: [-5, 5, -5, 5, 0],
                            transition: {
                              duration: 0.4,
                              ease: "easeInOut"
                            }
                          }
                        }}
                        whileFocus="focus"
                        animate={errors.number ? "error" : ""}
                      >
                        <Input
                          placeholder="Enter your number"
                          value={formData.number}
                          onChange={(e) =>
                            handleInputChange("number", e.target.value)
                          }
                          className={`${errors.number ? "border-red-500" : "border-[#d0d5db]"
                            } transition-colors duration-200`}
                          disabled={isSubmitting}
                        />
                      </motion.div>
                      <AnimatePresence>
                        {errors.number && (
                          <motion.span
                            className="text-red-500 text-xs"
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.2 }}
                          >
                            {errors.number}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </motion.div>

                  {/* Email */}
                  <motion.div
                    className="flex flex-col gap-2"
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 30,
                        scale: 0.95
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    <Label>
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <motion.div
                      variants={{
                        focus: {
                          scale: 1.02,
                          transition: {
                            duration: 0.2,
                            ease: "easeInOut"
                          }
                        },
                        error: {
                          x: [-5, 5, -5, 5, 0],
                          transition: {
                            duration: 0.4,
                            ease: "easeInOut"
                          }
                        }
                      }}
                      whileFocus="focus"
                      animate={errors.email ? "error" : ""}
                    >
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className={`${errors.email ? "border-red-500" : "border-[#d0d5db]"
                          } transition-colors duration-200`}
                        disabled={isSubmitting}
                      />
                    </motion.div>
                    <AnimatePresence>
                      {errors.email && (
                        <motion.span
                          className="text-red-500 text-xs"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          transition={{ duration: 0.2 }}
                        >
                          {errors.email}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Category */}
                  <motion.div
                    className="flex flex-col gap-2"
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 30,
                        scale: 0.95
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    <Label>Category</Label>
                    <motion.select
                      value={formData.category}
                      onChange={(e) =>
                        handleInputChange("category", e.target.value)
                      }
                      className="border border-[#d0d5db] rounded-lg p-2 disabled:opacity-50 transition-all duration-200"
                      disabled={isSubmitting}
                      whileFocus={{ scale: 1.01 }}
                    >
                      <option value="">Select your category</option>
                      <option value="student">Student</option>
                      <option value="teacher">Teacher</option>
                      <option value="working_professional">
                        Working Professional
                      </option>
                    </motion.select>
                  </motion.div>

                  {/* Message */}
                  <motion.div
                    className="flex flex-col gap-2"
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 30,
                        scale: 0.95
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut"
                        }
                      }
                    }}
                  >
                    <Label>Message</Label>
                    <motion.div
                      variants={{
                        focus: {
                          scale: 1.02,
                          transition: {
                            duration: 0.2,
                            ease: "easeInOut"
                          }
                        },
                        error: {
                          x: [-5, 5, -5, 5, 0],
                          transition: {
                            duration: 0.4,
                            ease: "easeInOut"
                          }
                        }
                      }}
                      whileFocus="focus"
                    >
                      <Textarea
                        placeholder="Please describe your question or issue..."
                        value={formData.message}
                        onChange={(e) =>
                          handleInputChange("message", e.target.value)
                        }
                        disabled={isSubmitting}
                        className="transition-all duration-200"
                      />
                    </motion.div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div variants={{
                    hidden: {
                      opacity: 0,
                      y: 30,
                      scale: 0.95
                    },
                    visible: {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut"
                      }
                    }
                  }}>
                    <motion.div
                      variants={{
                        idle: {
                          scale: 1,
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                        },
                        hover: {
                          scale: 1.05,
                          boxShadow: "0 8px 25px rgba(255, 191, 0, 0.4)",
                          transition: {
                            duration: 0.2,
                            ease: "easeInOut"
                          }
                        },
                        tap: {
                          scale: 0.98
                        },
                        loading: {
                          scale: [1, 1.02, 1],
                          transition: {
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }
                        }
                      }}
                      initial="idle"
                      whileHover={!isSubmitting ? "hover" : ""}
                      whileTap={!isSubmitting ? "tap" : ""}
                      animate={isSubmitting ? "loading" : "idle"}
                    >
                      <Button
                        type="submit"
                        className="bg-[#ffbf00] hover:bg-[#ffbf00]/90 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                        disabled={isSubmitting}
                      >
                        <AnimatePresence mode="wait">
                          {isSubmitting ? (
                            <motion.div
                              className="flex items-center justify-center gap-2"
                              key="loading"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <motion.div
                                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              />
                              <span className="text-white">Sending...</span>
                            </motion.div>
                          ) : (
                            <motion.div
                              className="flex items-center justify-center gap-2"
                              key="idle"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <motion.div
                                whileHover={{ x: 5 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Send className="w-4 h-4 text-white" />
                              </motion.div>
                              <span className="text-white">Send Message</span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Button>
                    </motion.div>
                  </motion.div>
                </motion.form>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};